import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface PermissionGuardProps {
  permission: string;
  children: ReactNode;
}

/**
 * Wraps a route element. If the admin doesn't have the required permission,
 * redirects to /dashboard and shows a toast.
 */
export function PermissionGuard({ permission, children }: PermissionGuardProps) {
  const { hasAccess, isLoading } = usePermissions();
  const navigate = useNavigate();

  const allowed = hasAccess(permission);

  useEffect(() => {
    if (!isLoading && !allowed) {
      toast.error("You don't have access to this page");
      navigate('/dashboard', { replace: true });
    }
  }, [isLoading, allowed, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!allowed) return null;

  return <>{children}</>;
}
